import styled from '@emotion/styled';
import { Button, Spin } from 'antd';
import { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useSetAtom } from 'jotai';
import Empty from 'components/Empty';
import { buyerNickMask } from 'services/channels/demo';
import TradeComponent from 'views/Conversation/Sider/TradeComponent';
import { TradeListContext } from 'views/Conversation/Sider/TradeContext';
import useTrade from 'views/Conversation/Sider/useTrade';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import { SiderRefContext } from 'views/Conversation/Sider/RefContext';
import { HeadComponent } from 'views/Conversation/Sider/Head';
import { useReviewContext } from './Context';
import useBuyerDetail from './hooks/useBuyerDetail';
import { reviewTrade } from './hooks/useNewReview';
import { useTradeOverThreeMonthEnable } from 'hooks/business/useOrgConfig';
import { reviewChannelMap } from 'utils/constants';
import { useIsLightChatLayout } from 'hooks/useChatLayoutType';
import { LightTradeList } from './LightTradeList';
import { LightChatSideContext } from 'components/LightConversation/contexts/LightChatSide';

const Container = styled.div`
  --main-color: var(--fett-app-primary-color);
  position: relative;
  height: 100%;
  overflow: auto;
`;

const TradeTitle = styled.div`
  height: 24px;
  line-height: 24px;
  text-align: center;
  font-size: 12px;
  color: var(--fett-scenario-accent-text);
  background: #ebf1ff;
  [data-color-mode='dark'] & {
    color: #8aadff;
    background: rgba(51, 102, 255, 0.16);
  }
`;

export function ReviewSider(props: HTMLAttributes<HTMLDivElement>) {
  const { conversation, detail } = useReviewContext();

  const { buyerId: externalBuyerId, internalStoreId, orderId, channel, region } = detail;

  const platform = reviewChannelMap[channel];

  const { t } = useTranslate();

  const isLightChatLayout = useIsLightChatLayout();
  const TRADE_OVER_THREE_MONTH = useTradeOverThreeMonthEnable();

  const setReviewTrade = useSetAtom(reviewTrade);

  const {
    loading: buyerDetailLoading,
    item: buyerDetail,
    fetch: fetchBuyerDetail,
  } = useBuyerDetail();

  const useTradeResult = useTrade(internalStoreId);

  const {
    list: tradeList,
    loading: tradeListLoading,
    fetchList: fetchTradeList,
    setList: setTradeList,
  } = useTradeResult;

  const [error, setError] = useState(false);

  const loading = buyerDetailLoading || tradeListLoading;

  const containerRef = useRef<HTMLDivElement>(null);

  const refsCurrent = {
    fetchBuyerDetail,
    fetchTradeList,
    t,
    setReviewTrade,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { fetchBuyerDetail, fetchTradeList, setReviewTrade },
    } = refs;

    setReviewTrade(null);

    fetchBuyerDetail({
      orderId,
      storeId: internalStoreId,
      channel,
      region,
    }).catch(() => {});

    fetchTradeList(
      {
        page_no: 1,
        page_size: 1,
        external_buyer_id: externalBuyerId,
        internal_store_id: internalStoreId,
        tunnelAccountId: conversation?.tunnelAccountId,
        external_trade_ids: orderId,
      },
      platform,
    )
      .then(() => {
        setError(false);
      })
      .catch(() => {
        setError(true);
      });
  }, [
    orderId,
    channel,
    region,
    internalStoreId,
    externalBuyerId,
    platform,
    conversation?.tunnelAccountId,
  ]);

  useEffect(() => {
    if (tradeList.length > 0) {
      const {
        current: { setReviewTrade },
      } = refs;
      setReviewTrade(tradeList[0]);
    }
  }, [tradeList]);

  if (loading) {
    return (
      <Container ref={containerRef} {...props}>
        <Spin spinning />
      </Container>
    );
  }

  if (!buyerDetail) {
    return (
      <Container ref={containerRef} {...props}>
        <Empty
          description={
            <>
              <p>{t('scene.reviewWorkspace.detail.error.loadingFailed')}</p>
              <Button
                type="primary"
                onClick={() => {
                  fetchBuyerDetail({
                    orderId,
                    storeId: internalStoreId,
                    channel,
                    region,
                  }).catch(() => {});
                }}
              >
                {t('scene.reviewWorkspace.detail.action.reload')}
              </Button>
            </>
          }
        />
      </Container>
    );
  }

  const {
    buyerDetail: { buyerPicture, buyerName, storeName, aliasName },
  } = buyerDetail;

  return (
    <Container ref={containerRef} {...props}>
      <HeadComponent
        storeId={internalStoreId}
        info={{
          buyerAvatar: buyerPicture,
          buyerNick: buyerNickMask(buyerName),
          channel: reviewChannelMap[channel],
          region,
          storeName,
          storeAlias: aliasName,
        }}
      />
      {tradeList.length ? (
        isLightChatLayout ? (
          <LightChatSideContext.Provider value={{ containerRef: containerRef }}>
            <TradeTitle>{t('scene.reviewWorkspace.detail.label.currentReview')}</TradeTitle>
            <LightTradeList tradeList={tradeList} conversation={conversation} />
          </LightChatSideContext.Provider>
        ) : (
          <SiderRefContext.Provider value={containerRef}>
            <PreviewModalProvider>
              <TradeListContext.Provider
                value={{
                  ...useTradeResult,
                  conversation,
                }}
              >
                <TradeTitle>{t('scene.reviewWorkspace.detail.label.currentReview')}</TradeTitle>
                <TradeComponent
                  trade={tradeList[0]}
                  onUpdate={(trade) => {
                    setTradeList((curList) => {
                      return curList.map((curTrade) => {
                        if (curTrade.internalTradeId === trade.internalTradeId) {
                          return trade;
                        }

                        return curTrade;
                      });
                    });
                  }}
                  conversation={conversation}
                  sendable={false}
                  reviewId={detail.id}
                />
              </TradeListContext.Provider>
            </PreviewModalProvider>
          </SiderRefContext.Provider>
        )
      ) : (
        <Empty
          description={
            error ? (
              <>
                <p>{t('scene.reviewWorkspace.detail.error.loadingFailed')}</p>
                <Button
                  type="primary"
                  onClick={() => {
                    fetchTradeList(
                      {
                        page_no: 1,
                        page_size: 1,
                        external_buyer_id: externalBuyerId,
                        internal_store_id: internalStoreId,
                        tunnelAccountId: conversation?.tunnelAccountId,
                        external_trade_ids: orderId,
                      },
                      platform,
                    )
                      .then(() => {
                        setError(false);
                      })
                      .catch(() => {
                        setError(true);
                      });
                  }}
                >
                  {t('scene.reviewWorkspace.detail.action.reload')}
                </Button>
              </>
            ) : platform === 'SHOPEE' && !TRADE_OVER_THREE_MONTH ? (
              t('scene.reviewWorkspace.detail.hint.shopeeHistoricalOrder')
            ) : (
              t('scene.reviewWorkspace.detail.empty.tradeList')
            )
          }
        />
      )}
    </Container>
  );
}
